import { useCallback, useContext, useRef } from 'react';

import { AppContext, DesignActionTypes } from '@context';
import { lang } from '@lang';
import { saveCustomerBlueprint } from '@external';
import { selectNavigation, useAppSelector } from '@state';
import { useAppToast } from './use-app-toast.hook';
import { useDesign } from './use-design';
import { useKeystoneSession } from '../auth/use-keystone-session';

/**
 * Feeds RtaBlueprintComponent's `onBlueprintExported`: downloading a blueprint PDF/PPTX saves the
 * design (#607). The export is what stamps the sheet's creation and revision dates, so a download
 * that is never saved leaves the issued document carrying a date the design does not hold — and the
 * next download stamps a fresh one. Saves the open version in place: no draft promotion and no
 * reload, unlike the Save button.
 */
export const useSaveOnBlueprintExport = (): (() => Promise<void>) => {
	const [controller] = useDesign();
	const { getToken } = useKeystoneSession();
	const { appDispatch } = useContext(AppContext);
	const appNav = useAppSelector(selectNavigation);
	const appToast = useAppToast();

	// This callback is a prop on the memoized blueprint view, so it has to keep one identity for the
	// session — what it needs rides in a ref rather than in a dependency array that re-renders the sheet.
	const latest = useRef({ controller, getToken, appDispatch, appNav, appToast });
	latest.current = { controller, getToken, appDispatch, appNav, appToast };

	return useCallback(async (): Promise<void> => {
		const { controller: design, getToken: token, appDispatch: dispatch, appNav: nav, appToast: toast } = latest.current;
		if (!design || design.readonly || !nav.email) return;

		const [, error] = await saveCustomerBlueprint({ controller: design, customerEmail: nav.email, mode: 'live', getToken: token });
		if (error) {
			toast.error({ title: lang.customer.message.saveFailed });
			return;
		}

		localStorage.removeItem(`autosave-${design.blueprint.id}`);
		design.kitchen.isLocal = false;
		dispatch({ type: DesignActionTypes.setIsDirty, payload: { id: nav.designId ?? '', isDirty: false } });
		toast.success({ title: lang.customer.message.saveSuccess });
	}, []);
};
